import * as THREE from 'three';
import { fbm2, mulberry32 } from '../../core/rng';
import type { BuiltLocation, LocationDef } from '../Location';
import { paintableSnowMaterial, registerPaintable } from '../paintable';
import { box, collider, disposeScene, skyDome, stdMat, sun } from '../props';
import { createTerrain } from '../terrain';

/** Snow falling on sand: a frozen shoreline, a boardwalk, palm trees dusted
 * white. Snow thins out near the waterline (paint only sticks to snow). */
export const beachLocation: LocationDef = {
  id: 'beach',
  name: 'Frozen Beach',
  description: 'Snow on sand and palms in white coats. The strangest winter postcard.',
  thumbnail: { skyTop: '#a8c4dd', skyBottom: '#eef3f7', ground: '#e8ddc4', accent: '#27506b' },

  build(ctx): BuiltLocation {
    const scene = new THREE.Scene();
    const colliders: THREE.Box3[] = [];
    const paintables: BuiltLocation['paintables'] = [];

    scene.fog = new THREE.FogExp2(0xdde7ee, 0.0065);
    scene.add(
      skyDome([
        [0, '#8fb3d4'],
        [0.65, '#c9dcea'],
        [1, '#f2f6f8'],
      ]),
    );
    scene.add(new THREE.HemisphereLight(0xc5d8e8, 0xd8cdb4, 0.9));
    scene.add(sun(0xfff8e8, 1.25, 40, 70, -50, 80));

    // Water starts around z = 34; dunes fade to a flat wet strip before it.
    const shore = (z: number) => THREE.MathUtils.smoothstep(z, 8, 34);
    const height = (x: number, z: number) => {
      const dunes = fbm2(x * 0.045, z * 0.045, ctx.seed) * 1.6 + 0.4;
      return THREE.MathUtils.lerp(dunes, -0.55, shore(z));
    };
    // Snow cover: full inland, thin near the waterline.
    const snowMask = (x: number, z: number) =>
      THREE.MathUtils.clamp(
        1 - shore(z - 4) + (fbm2(x * 0.1, z * 0.1, ctx.seed + 9) - 0.5) * 0.25,
        0,
        1,
      );

    const snow = paintableSnowMaterial({
      grainScale: 1300,
      useSnowMask: true,
      bareColor: 0xcdb488,
    });
    const terrain = createTerrain({
      size: 150,
      segments: 120,
      height,
      material: snow.material,
      snowMask,
    });
    scene.add(terrain.mesh);
    registerPaintable(paintables, 'ground', terrain.mesh, snow.uniform, true);

    // Sea with a frozen margin.
    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(320, 90),
      new THREE.MeshStandardMaterial({ color: 0x27506b, roughness: 0.12, metalness: 0.1 }),
    );
    water.geometry.rotateX(-Math.PI / 2);
    water.position.set(0, -0.42, 80);
    scene.add(water);
    const iceMargin = new THREE.Mesh(
      new THREE.PlaneGeometry(320, 14),
      new THREE.MeshStandardMaterial({
        color: 0xcfe0ec,
        roughness: 0.25,
        transparent: true,
        opacity: 0.9,
      }),
    );
    iceMargin.geometry.rotateX(-Math.PI / 2);
    iceMargin.position.set(0, -0.38, 40);
    scene.add(iceMargin);

    // Boardwalk running along the shore.
    const walk = new THREE.Group();
    const plankMat = stdMat(0x8a6f52, 0.9);
    for (let i = 0; i < 26; i++) {
      walk.add(box(2.6, 0.08, 0.62, plankMat, 0, 0, i * 0.66 - 8));
    }
    for (const [px, pz] of [
      [-1.1, -8],
      [1.1, -8],
      [-1.1, 0],
      [1.1, 0],
      [-1.1, 8.6],
      [1.1, 8.6],
    ] as const) {
      walk.add(box(0.16, 1.4, 0.16, stdMat(0x6e5642, 0.9), px, -0.7, pz));
    }
    walk.rotation.y = Math.PI / 2;
    walk.position.set(-2, 0.75, 20);
    scene.add(walk);
    colliders.push(collider(-2, 20, 18, 2.8, -0.5, 0.9));

    // Palms dusted with snow.
    const rand = mulberry32(ctx.seed + 3);
    const palm = (x: number, z: number) => {
      const g = new THREE.Group();
      const trunkMat = stdMat(0x8a7355, 0.9);
      let px = 0;
      const lean = (rand() - 0.5) * 0.5;
      for (let i = 0; i < 5; i++) {
        const seg = new THREE.Mesh(
          new THREE.CylinderGeometry(0.13 - i * 0.012, 0.15 - i * 0.012, 1.1, 8),
          trunkMat,
        );
        px += lean * 0.22;
        seg.position.set(px, 0.5 + i * 1.02, 0);
        seg.rotation.z = -lean * 0.35;
        seg.castShadow = true;
        g.add(seg);
      }
      const frondMat = stdMat(0x4a7a54, 0.9);
      const dustMat = stdMat(0xe9eef6, 0.95);
      // Fronds droop outward from the crown; each is a squashed cone pointing
      // radially out and slightly down, with a snow-dust cone on top.
      const frondGeo = new THREE.ConeGeometry(0.3, 2.4, 4);
      frondGeo.scale(1, 1, 0.22);
      frondGeo.translate(0, 1.1, 0);
      const dustGeo = new THREE.ConeGeometry(0.22, 2.0, 4);
      dustGeo.scale(1, 1, 0.18);
      dustGeo.translate(0, 1.25, 0.06);
      for (let i = 0; i < 7; i++) {
        const angle = (i / 7) * Math.PI * 2 + rand() * 0.3;
        const holder = new THREE.Group();
        holder.position.set(px, 5.2, 0);
        holder.rotation.y = angle;
        const frond = new THREE.Mesh(frondGeo, frondMat);
        frond.rotation.x = 1.95 + rand() * 0.25; // tip outward-down
        holder.add(frond);
        const dust = new THREE.Mesh(dustGeo, dustMat);
        dust.rotation.x = frond.rotation.x - 0.06;
        holder.add(dust);
        g.add(holder);
      }
      g.position.set(x, height(x, z) - 0.15, z);
      colliders.push(collider(x, z, 0.5, 0.5, 0, height(x, z) + 5));
      return g;
    };
    for (const [x, z] of [
      [-18, 6],
      [-10, -12],
      [0, -18],
      [16, 10],
      [24, -14],
      [-28, -6],
    ] as const) {
      scene.add(palm(x, z));
    }

    return {
      scene,
      spawn: { pos: new THREE.Vector3(6, height(6, -6), -6), yaw: Math.PI },
      paintables,
      colliders,
      groundHeight: height,
      groundSurfaceIdAt: (x, z) => (snowMask(x, z) > 0.25 ? 'ground' : ''),
      bounds: { minX: -70, maxX: 70, minZ: -70, maxZ: 31 },
      baseFogDensity: 0.0065,
      dispose() {
        disposeScene(scene);
      },
    };
  },
};
